import { Field, Form, Formik } from 'formik';
import { Map } from 'immutable';
import _ from 'lodash';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { useDebouncedCallback } from 'use-debounce';
import { v4 as uuidv4 } from 'uuid';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { dateSelectors } from 'app-components/flux/date';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { filtersActions, filtersTypes } from 'app-components/flux/filters';
import {
  spendingWatchlistActions,
  spendingWatchlistSelectors,
  spendingWatchlistTypes,
} from 'app-components/flux/spending-watchlist';
import {
  getWatchlistDateBounds,
  isWatchlistRangeAllowed,
  WATCHLIST_MAX_RANGE_YEARS,
} from 'app-components/forms/watchlist/watchlistForm';
import { tracker } from 'app-components/utils/core';

import QAmountField from 'components/QAmountField';
import QEmojiSelect from 'components/QEmojiSelect';
import SDatePicker from 'components/SDatePicker';
import TypeSelectField from './TypeSelectField';

const useStyles = makeStyles()(({ spacing }) => ({
  root: {
    padding: '16px 0 0 176px',
    flexDirection: 'column',
  },
  typeContainer: {
    width: '100%',
  },
  nameContainer: {
    flex: 1,
  },
  emojiContainer: {
    width: 80,
    '& > div': { marginTop: 0 },
    '& .MuiFilledInput-root': {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
      borderRightWidth: 0,
    },
  },
  showTargetAmountGroup: {
    flexDirection: 'row',
    marginTop: 4,
    '& label + label': {
      marginLeft: spacing(2),
    },
  },
  targetAmountInput: {
    textAlign: 'left',
  },
  timeRangeToggleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: spacing(1.5),
  },
  timeRangeSwitch: {
    margin: 0,
  },
  datePickerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    marginTop: spacing(2),
  },
}));

const filterKeyObj = {
  CHARTOFACCOUNT: 'chartOfAccounts',
  PAYEE: 'payees',
  TAG: 'tags',
};

function WatchlistForm(props) {
  const { typesData, watchlist, isEditing, filter, setFormData, overwriteInitialData, setSuccessModal, onSubmit } =
    props;
  const { classes } = useStyles();

  const [debouncedShareFormikProps] = useDebouncedCallback((formData) => setFormData?.(formData), 300);
  // Gates the entire time range UI (toggle + date pickers). When false the fields still exist
  // in Formik but are never rendered and are stripped from the API payload in onCreate/onUpdate.
  const showTimeTracking = useSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'watchlistTimeSelector'));
  const watchlistList = useSelector(spendingWatchlistSelectors.getSpendingWatchLists);
  const todayStart = useSelector(dateSelectors.getCurrentDate);

  const dispatch = useDispatch();

  const initialValues = useMemo(() => {
    if (overwriteInitialData) {
      const typeId = overwriteInitialData.filter.type;
      const filterTypeKey = filterKeyObj[typeId];
      const resolvedType = typeof filter === 'string' ? filter : typeId;
      return {
        ...overwriteInitialData,
        filter: {
          ...overwriteInitialData.filter,
          type: resolvedType,
          [filterTypeKey]: overwriteInitialData.filter?.[filterTypeKey] || [],
        },
      };
    }

    // Guard against missing date fields on a FIXED watchlist (e.g. data migration gaps).
    // Storing as ISO strings (not DateTime objects) so Formik's reference-equality dirty check works correctly.
    const isFixedPeriod = isEditing && watchlist.period === 'FIXED';
    return {
      name: isEditing ? watchlist.name : '',
      emoji: isEditing ? watchlist.emoji : '',
      filter: isEditing ? filter.filterItems[0] : { type: filter },
      showTargetAmount: (isEditing ? Boolean(watchlist.targetAmount != null) : false).toString(),
      targetAmount: isEditing && watchlist.targetAmount != null ? watchlist.targetAmount.toString() : '0.00',
      // Time range fields live in Formik so dirty tracking, reset, and back-navigation state preservation
      // all work automatically. They are undefined/null for new watchlists and populated from the
      // saved period/dates when editing.
      timeRangeEnabled: isFixedPeriod,
      startDate: isFixedPeriod && watchlist.startDate ? DateTime.fromISO(watchlist.startDate).toISODate() : null,
      endDate: isFixedPeriod && watchlist.endDate ? DateTime.fromISO(watchlist.endDate).toISODate() : null,
    };
  }, [watchlist, isEditing, filter, overwriteInitialData]);

  // Grandfathered against the stored record, never `initialValues`: the Back button stashes live
  // values as `overwriteInitialData`, which would let a typed range grandfather itself. Undefined
  // when creating, since there is no saved range.
  const savedRange = useMemo(
    () =>
      isEditing && watchlist?.period === 'FIXED'
        ? { startDate: watchlist.startDate ?? null, endDate: watchlist.endDate ?? null }
        : undefined,
    [isEditing, watchlist],
  );

  // Stable slot config shared by both date pickers: rounds day cells and anchors the popper
  // below-start without flipping so it stays predictable near viewport edges.
  const datePickerSlotProps = useMemo(
    () => ({
      day: {
        sx: (t) => ({
          borderRadius: '8px',
          '&.Mui-selected': { backgroundColor: t.palette.primary.main },
          '&.Mui-selected:hover, &.Mui-selected:focus': { backgroundColor: t.palette.primary.main },
        }),
      },
      popper: {
        placement: 'bottom-start',
        modifiers: [{ name: 'offset', options: { offset: [0, 8] } }],
      },
    }),
    [],
  );

  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  const renderForm = useCallback(
    (formikProps) => {
      const genericOnChange = (field, value) => formikProps.setFieldValue(field.name, value);

      const alreadyUsedWatchlistName = (newName) => {
        const nameToUse = newName || formikProps.values.name;
        let isNameUsed = watchlistList.find((p) => p.name === nameToUse?.trim()) !== undefined;
        if (isEditing && formikProps.values?.name === watchlist?.name) {
          isNameUsed = false;
        }
        return isNameUsed;
      };

      const typeId = formikProps.values.filter?.type;
      const filterTypeKey = filterKeyObj[typeId];
      const dynamicFieldValue = formikProps.values.filter?.[filterTypeKey];

      // Derive time range display values from Formik values. Storing as ISO strings in Formik
      // keeps dirty tracking reliable; converting back to DateTime here for picker display and comparison.
      const { timeRangeEnabled } = formikProps.values;
      const startDate = formikProps.values.startDate ? DateTime.fromISO(formikProps.values.startDate) : null;
      const endDate = formikProps.values.endDate ? DateTime.fromISO(formikProps.values.endDate) : null;

      // endDate equal to startDate is intentionally allowed (single-day tracking)
      const endDateBeforeStart =
        timeRangeEnabled && startDate?.isValid && endDate?.isValid && endDate.startOf('day') < startDate.startOf('day');

      // Backstop for a typed date; the pickers below are already bounded. Shared with mobile's form.
      const currentRange = { startDate: formikProps.values.startDate, endDate: formikProps.values.endDate };
      const rangeTooLong =
        timeRangeEnabled &&
        startDate?.isValid &&
        endDate?.isValid &&
        !isWatchlistRangeAllowed(currentRange, savedRange);

      const { minimumStartDate, maximumEndDate } = getWatchlistDateBounds(
        currentRange,
        todayStart.toISODate(),
        savedRange,
      );

      const isTimeRangeInvalid =
        showTimeTracking &&
        timeRangeEnabled &&
        (!startDate?.isValid || !endDate?.isValid || endDateBeforeStart || rangeTooLong);

      const isNameEmpty = !formikProps.values.name || formikProps.values.name.trim().length === 0;
      const isDynamicFieldEmpty = !Array.isArray(dynamicFieldValue) || dynamicFieldValue.length === 0;
      const isDuplicateName = alreadyUsedWatchlistName();
      const hasOldInvalidConditions = !formikProps.isValid || (!formikProps.dirty && !overwriteInitialData);

      const [singleType, pluralType, qResPickerKey] = typesData[typeId];

      // Not gated on isTimeRangeInvalid: range errors only render once submitCount is non-zero, so
      // disabling here would trade a visible message for a dead button. handleSubmit is the backstop.
      const isDisabled = isNameEmpty || isDynamicFieldEmpty || isDuplicateName || hasOldInvalidConditions;

      // Share form state with WatchlistModal so it can drive the submit button in DialogActions
      // and the discard-changes alert.
      debouncedShareFormikProps({
        dirty: formikProps.dirty,
        showFieldsNeedAttention: !endDatePickerOpen && formikProps.submitCount > 0 && isTimeRangeInvalid,
        isSubmitting: formikProps.isSubmitting,
        isValid: formikProps.isValid,
        isValidating: formikProps.isValidating,
        resetForm: formikProps.resetForm,
        submitForm: formikProps.submitForm,
        values: formikProps.values,
        isDisabled,
      });

      return (
        <Form>
          <Box sx={{ maxWidth: 416, pt: 2, pb: 2 }}>
            <Grid item container direction='column'>
              <Grid item className={classes.typeContainer}>
                {isEditing ? (
                  <Field name='filter.type'>
                    {({ field, form: { touched, errors } }) => (
                      <TextField
                        {...field}
                        id='watchlist-input-typeId'
                        label='Type'
                        error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                        helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                        InputProps={{ autoComplete: 'off' }}
                        variant='filled'
                        margin='normal'
                        fullWidth
                        select
                      >
                        {Object.keys(typesData).map((typeKey) => (
                          <MenuItem key={typeKey} value={typeKey}>
                            By {typesData[typeKey][0]}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Field>
                ) : (
                  <>
                    <Typography variant='titleMedium'>By {singleType}</Typography>
                    <Typography variant='paragraphRegular' color='textSecondary'>
                      Keep an eye on your spending in one or more {pluralType}
                    </Typography>
                  </>
                )}
              </Grid>
              <Grid item container direction='row' alignItems='flex-start' sx={{ mt: 3 }}>
                <Grid item className={classes.emojiContainer}>
                  <Field name='emoji'>
                    {({ field, form: { touched, errors } }) => (
                      <QEmojiSelect
                        {...field}
                        id='watchlist-input-emoji'
                        onChange={(value) => genericOnChange(field, value)}
                        error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                        helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                      />
                    )}
                  </Field>
                </Grid>
                <Grid item className={classes.nameContainer}>
                  <Field name='name'>
                    {({ field, form: { touched, errors } }) => (
                      <TextField
                        {...field}
                        id='watchlist-input-name'
                        label='Name'
                        error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                        helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                        InputProps={{ autoComplete: 'off' }}
                        variant='filled'
                        margin='none'
                        fullWidth
                        autoFocus={!isEditing}
                        sx={{ '& .MuiFilledInput-root': { borderTopLeftRadius: 0, borderBottomLeftRadius: 0 } }}
                      />
                    )}
                  </Field>
                </Grid>
              </Grid>
            </Grid>
            <Grid item container direction='column' sx={{ mt: 1 }}>
              <Field name={`filter.${filterTypeKey}`}>
                {({ field, form: { touched, errors } }) => (
                  <TypeSelectField
                    {...field}
                    id={`watchlist-input-select-${pluralType.toLowerCase()}`}
                    label={`Select ${pluralType}`}
                    onBlur={() => formikProps.setFieldTouched(`filter.${filterTypeKey}`)}
                    onChange={(value, name) => {
                      if (formikProps.values.name?.trim().length === 0) {
                        formikProps.setValues({
                          ...formikProps.values,
                          name: value[0]?.id === '0' ? 'Uncategorized' : name,
                          filter: { ...(isEditing ? initialValues.filter : { type: filter }), [filterTypeKey]: value },
                        });
                      } else {
                        genericOnChange(field, value);
                      }
                    }}
                    error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                    helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                    itemsType={qResPickerKey}
                    filter={formikProps.values.filter}
                    filterTypeKey={filterTypeKey}
                  />
                )}
              </Field>
            </Grid>
            {showTimeTracking && (
              <>
                <Grid item container direction='column' sx={{ mt: 3 }}>
                  <div className={classes.timeRangeToggleRow}>
                    <Typography variant='bodyRegular'>Time range</Typography>
                    <Switch
                      checked={timeRangeEnabled}
                      className={classes.timeRangeSwitch}
                      id='watchlist-time-range-toggle'
                      inputProps={{ 'aria-label': 'Time range' }}
                      size='small'
                      onChange={(e) => {
                        const enabled = e.target.checked;
                        formikProps.setFieldValue('timeRangeEnabled', enabled);
                        if (enabled) {
                          // Default both dates to today when the user first enables a time range
                          const today = todayStart.toISODate();
                          formikProps.setFieldValue('startDate', today);
                          formikProps.setFieldValue('endDate', today);
                        } else {
                          formikProps.setFieldValue('startDate', null);
                          formikProps.setFieldValue('endDate', null);
                        }
                      }}
                    />
                  </div>
                  <Typography variant='paragraphRegular' color='textSecondary' sx={{ mt: 0.5 }}>
                    Track spending for a specific period, like a holiday or vacation.
                  </Typography>
                  {timeRangeEnabled && (
                    <div className={classes.datePickerRow}>
                      <Box sx={{ flex: 1 }}>
                        <SDatePicker
                          id='watchlist-start-date'
                          label='Start date'
                          value={startDate}
                          onChange={(date) =>
                            formikProps.setFieldValue('startDate', date?.isValid ? date.toISODate() : null)
                          }
                          minDate={minimumStartDate ? DateTime.fromISO(minimumStartDate) : undefined}
                          helperText=' '
                          textFieldProps={{ fullWidth: true }}
                          slotProps={datePickerSlotProps}
                        />
                      </Box>
                      <Typography variant='bodyRegular'>–</Typography>
                      <Box sx={{ flex: 1 }}>
                        <SDatePicker
                          id='watchlist-end-date'
                          label='End date'
                          value={endDate}
                          onChange={(date) =>
                            formikProps.setFieldValue('endDate', date?.isValid ? date.toISODate() : null)
                          }
                          onOpen={() => setEndDatePickerOpen(true)}
                          onClose={() => setEndDatePickerOpen(false)}
                          maxDate={maximumEndDate ? DateTime.fromISO(maximumEndDate) : undefined}
                          error={
                            !endDatePickerOpen && formikProps.submitCount > 0 && (endDateBeforeStart || rangeTooLong)
                          }
                          helperText={
                            !endDatePickerOpen && formikProps.submitCount > 0
                              ? (endDateBeforeStart && 'Must be after the start date') ||
                                (rangeTooLong && `Date range cannot exceed ${WATCHLIST_MAX_RANGE_YEARS} years`) ||
                                ' '
                              : ' '
                          }
                          textFieldProps={{ fullWidth: true }}
                          slotProps={datePickerSlotProps}
                        />
                      </Box>
                    </div>
                  )}
                </Grid>
                <Divider sx={{ mt: 3, mb: 0 }} />
              </>
            )}
            <Grid item container direction='column' sx={{ mt: 3 }}>
              <Typography variant='bodyRegular'>{showTimeTracking ? 'Target amount' : 'Monthly target'}</Typography>
              <Field name='showTargetAmount'>
                {({ field }) => (
                  <RadioGroup
                    {...field}
                    id='watchlist-input-show-targetAmount'
                    aria-label={showTimeTracking ? 'Target amount' : 'Monthly target'}
                    className={classes.showTargetAmountGroup}
                  >
                    <FormControlLabel value='false' control={<Radio />} label="Don't Set" />
                    <FormControlLabel value='true' control={<Radio />} label='Set' />
                  </RadioGroup>
                )}
              </Field>
              {formikProps?.values?.showTargetAmount === 'true' && (
                <Field name='targetAmount'>
                  {({ field, form: { touched, errors } }) => (
                    <QAmountField
                      {...field}
                      label='Amount'
                      expectedSign='positive'
                      noNegatives
                      className={classes.targetAmountInput}
                      editable
                      id='watchlist-input-targetAmount'
                      fullWidth
                      margin='normal'
                      onChange={(e) => formikProps.setFieldValue('targetAmount', e?.target?.value)}
                      error={Boolean(_.get(touched, field.name) && _.get(errors, field.name))}
                      helperText={_.get(touched, field.name) && _.get(errors, field.name)}
                    />
                  )}
                </Field>
              )}
            </Grid>
          </Box>
        </Form>
      );
    },
    [
      debouncedShareFormikProps,
      datePickerSlotProps,
      endDatePickerOpen,
      overwriteInitialData,
      showTimeTracking,
      todayStart,
      typesData,
      classes,
      isEditing,
      watchlistList,
      watchlist?.name,
      // A useMemo over the same inputs, so naming it whole costs no extra renders.
      initialValues,
      savedRange,
      filter,
    ],
  );

  const onCreate = useCallback(
    (values, options = {}) => {
      const { showSuccessModal = true } = options;
      const newId = uuidv4().toUpperCase();
      const { type } = values.filter;
      const filterTypeKey = filterKeyObj[type];
      const targetAmount = values.showTargetAmount === 'true' ? values.targetAmount : null;
      // Convert stored ISO strings back to DateTime objects for the API call
      const startDate = values.startDate ? DateTime.fromISO(values.startDate) : null;
      const endDate = values.endDate ? DateTime.fromISO(values.endDate) : null;
      const usingTimeRange = showTimeTracking && values.timeRangeEnabled && startDate?.isValid && endDate?.isValid;
      const newList = Map({
        title: values.name.length >= 256 ? values.name.substring(0, 256).trim() : values.name.trim(),
        targetAmount,
        filterItems: filtersTypes.mkFilter({
          id: null,
          clientId: newId,
          type: 'SPENDINGWATCHLIST',
          filterItems: [{ type, [filterTypeKey]: values.filter[filterTypeKey] || [] }],
        }),
        emoji: values.emoji,
        // Only include date fields when actually using a time range — the API rejects null dates
        ...(usingTimeRange && {
          period: 'FIXED',
          startDate: startDate.toISO(),
          endDate: endDate.toISO(),
        }),
      });
      dispatch(spendingWatchlistActions.createWatchList(newList));
      if (showSuccessModal) {
        setSuccessModal?.('create');
      }
      let watchlistType;
      switch (typesData[values.filter.type][1]) {
        case 'Payees':
          watchlistType = 'payee';
          break;
        case 'Tags':
          watchlistType = 'tag';
          break;
        default:
          watchlistType = 'category';
      }
      tracker.track(tracker.events.watchlistCreate, {
        type: watchlistType,
        target: Boolean(targetAmount),
        emoji: !!values.emoji,
        number: values.filter?.[filterTypeKey]?.length || 0,
      });
    },
    [typesData, dispatch, setSuccessModal, showTimeTracking],
  );

  const onUpdate = useCallback(
    (values, options = {}) => {
      const { showSuccessModal = true } = options;
      const baseFilterData = { ...filter.toJS() };
      delete baseFilterData.id;
      delete baseFilterData.createdAt;
      const filterToCheck = { ...baseFilterData, filterItems: [values.filter] };
      if (JSON.stringify(baseFilterData) !== JSON.stringify(filterToCheck)) {
        const { type } = values.filter;
        const filterTypeKey = filterKeyObj[type];
        dispatch(
          filtersActions.updateFilter(
            {
              ...filter.toJS(),
              filterItems: [{ type, [filterTypeKey]: values.filter[filterTypeKey] || [] }],
            },
            { context: 'watchlist' },
          ),
        );
      }

      const resetTargetAmount = !values.targetAmount && values.targetAmount !== undefined;
      const updates = {};

      if (values.name.trim() !== watchlist.name.trim()) updates.name = values.name.trim();
      if (values.targetAmount !== watchlist.targetAmount && values.targetAmount !== null) {
        updates.targetAmount = values.targetAmount;
      }
      if (values.emoji !== watchlist.emoji) updates.emoji = values.emoji;
      if (resetTargetAmount || values.showTargetAmount === 'false') {
        updates.resetTargetAmount = true;
        updates.targetAmount = null;
      }

      if (showTimeTracking) {
        const currentPeriod = values.timeRangeEnabled ? 'FIXED' : 'MONTH';
        if (watchlist.period !== currentPeriod) {
          updates.period = currentPeriod;
        }
        if (currentPeriod === 'FIXED') {
          // Guard against missing date fields on the saved watchlist (e.g. data migration gaps)
          const prevStart =
            watchlist.period === 'FIXED' && watchlist.startDate ? DateTime.fromISO(watchlist.startDate) : null;
          const prevEnd =
            watchlist.period === 'FIXED' && watchlist.endDate ? DateTime.fromISO(watchlist.endDate) : null;
          const currentStart = values.startDate ? DateTime.fromISO(values.startDate) : null;
          const currentEnd = values.endDate ? DateTime.fromISO(values.endDate) : null;
          // Compare day boundaries (not raw timestamps) so a time-of-day change within
          // the same calendar day does not generate a spurious update.
          if (currentStart?.isValid && currentStart.startOf('day').valueOf() !== prevStart?.startOf('day')?.valueOf()) {
            updates.startDate = currentStart.toISO();
          }
          if (currentEnd?.isValid && currentEnd.startOf('day').valueOf() !== prevEnd?.startOf('day')?.valueOf()) {
            updates.endDate = currentEnd.toISO();
          }
        }
        // When switching to MONTH, only update period — don't send null dates since the API rejects them.
        // The server ignores stored dates when period is not FIXED.
      }

      dispatch(
        spendingWatchlistActions.updateSpendingWatchList(
          spendingWatchlistTypes.mkSpendingWatchList({ ...watchlist, ...updates }),
        ),
      );

      if (showSuccessModal) setSuccessModal?.('update');
      tracker.track(tracker.events.watchlistEdit);
    },
    [dispatch, filter, watchlist, setSuccessModal, showTimeTracking],
  );

  const handleSubmit = useCallback(
    (values) => {
      // isTimeRangeInvalid is scoped to renderForm, so re-derive it here before hitting the API.
      if (showTimeTracking && values.timeRangeEnabled) {
        const start = values.startDate ? DateTime.fromISO(values.startDate) : null;
        const end = values.endDate ? DateTime.fromISO(values.endDate) : null;
        if (!start?.isValid || !end?.isValid || end.startOf('day') < start.startOf('day')) {
          return;
        }
        // The saved range keeps one stored before the cap editable, matching the picker bounds.
        if (!isWatchlistRangeAllowed({ startDate: values.startDate, endDate: values.endDate }, savedRange)) {
          return;
        }
      }
      const submitDefault = isEditing ? onUpdate : onCreate;
      if (onSubmit) return onSubmit(values, submitDefault);
      return submitDefault(values);
    },
    [savedRange, isEditing, onCreate, onSubmit, onUpdate, showTimeTracking],
  );

  return (
    <Grid container className={classes.root}>
      <Formik
        key={`watchlist-form-${isEditing ? 'editing' : 'creating'}`}
        initialValues={initialValues}
        validateOnChange
        validate={(values) => {
          const errors = {};

          if (!values.filter?.type) {
            errors.filter = { type: 'required' };
          }

          if (!values?.name?.trim()) {
            errors.name = 'Required field';
          } else {
            const name = values.name.trim();
            if (!(isEditing && name === watchlist.name)) {
              if (watchlistList.find((p) => p.name === name)) {
                // name collision
                errors.name = 'The watchlist must have a unique name';
              }
            }
          }

          const typeKey = filterKeyObj[values?.filter?.type];
          if (!typeKey || !values.filter[typeKey]?.length) {
            errors.filter = { ...(errors.filter || {}), [typeKey]: 'Required field' };
          }

          if (values.showTargetAmount === 'true') {
            const amount = parseFloat(values.targetAmount);
            if (isNaN(amount) || amount <= 0) {
              errors.targetAmount = 'Must be greater than $0.00';
            }
          }

          return errors;
        }}
        onSubmit={handleSubmit}
      >
        {renderForm}
      </Formik>
    </Grid>
  );
}

WatchlistForm.propTypes = {
  typesData: PropTypes.object,
  watchlist: PropTypes.object,
  isEditing: PropTypes.bool,
  filter: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  setFormData: PropTypes.func,
  overwriteInitialData: PropTypes.object,
  setSuccessModal: PropTypes.func,
  onSubmit: PropTypes.func,
};

WatchlistForm.whyDidYouRender = true;

export default WatchlistForm;
